import { ref, shallowRef, toValue } from 'vue'

/**
 * The suggestion pools behind a PredefinedValuesInputComponent-style field:
 * admin-curated template options (template-option.get) and the user's saved
 * phrases ("Minu fraasid", user-phrase.*), keyed by category + name.
 *
 * Extracted from PredefinedValuesInputComponent (RED-268) so the rich-text
 * editor's phrases dropdown shares the exact same pools and semantics.
 */
export function usePredefinedValues(category, name) {
  /** @type {import('vue').Ref<Array<{label: string, value: number}>>} */
  const options = ref([])
  /** @type {import('vue').Ref<Array<{id: number, label: string}>>} */
  const userPhrases = ref([])
  const savingPhrase = shallowRef(false)

  const poolKey = () => ({ category: toValue(category), name: toValue(name) })

  const loadOptions = async () => {
    const response = await axios.get(route('template-option.get', poolKey()))
    options.value = response.data.map(row => ({
      label: row.value,
      value: row.id,
    }))
    return options.value
  }

  const loadUserPhrases = async () => {
    try {
      const response = await axios.get(route('user-phrase.index', poolKey()))
      userPhrases.value = response.data.map(phrase => ({
        id: phrase.id,
        label: phrase.value,
      }))
    } catch {
      // Silently fail — user phrases are non-critical
    }
  }

  const saveAsPhrase = async value => {
    const trimmed = value?.trim()
    if (!trimmed || savingPhrase.value) return null
    savingPhrase.value = true
    try {
      const response = await axios.post(route('user-phrase.store'), {
        ...poolKey(),
        value: trimmed,
      })
      const phrase = { id: response.data.id, label: response.data.value }
      userPhrases.value.unshift(phrase)
      return phrase
    } finally {
      savingPhrase.value = false
    }
  }

  const deletePhrase = async phrase => {
    await axios.delete(route('user-phrase.destroy', { userPhrase: phrase.id }))
    userPhrases.value = userPhrases.value.filter(p => p.id !== phrase.id)
  }

  return {
    options,
    userPhrases,
    savingPhrase,
    loadOptions,
    loadUserPhrases,
    saveAsPhrase,
    deletePhrase,
  }
}
